interface MUIObject {
  currentCSelections?: () => string
  // Called with the full c_selections string (one argument); returns Promise<string> with updated c_selections
  navigateSpaAction?: (cSelections: string) => Promise<string>
  onNavUpdated?: (callback: (err: any, newCSelections: string) => void) => void
  _app_name?: string
  [key: string]: any
}

interface GlobalThis {
  MUI?: MUIObject
}

let keepAliveTimeout: NodeJS.Timeout | null = null
let nrOfFailedConnections = 0
const KEEP_ALIVE_INTERVAL = 30000
const SPRING_ERROR_MSG =
  'This session has been expired (possibly due to multiple concurrent logins being attempted as the same user).'

// Checkmate action promise chain (ensures actions are emitted sequentially)
let checkmateActionPromise: Promise<void> | null = null

export const isCheckmated = (): boolean => {
  // Exact legacy pattern: check for MUI existence only
  return (
    typeof (globalThis as any).MUI !== 'undefined' &&
    !!(globalThis as any).MUI &&
    Object.keys((globalThis as any).MUI)?.length !== 0
  )
}

export const getApiUrl = (): string => {
  return document.documentElement.getAttribute('api-url') ?? import.meta.env.VITE_API_URL ?? '/be'
}

export const getIMedidataLoginUrl = (): string => {
  return document.documentElement.getAttribute('login-imedidata-url') ?? ''
}

export const getCheckmatedApiUrl = (): string => {
  return isCheckmated() ? (document.documentElement.getAttribute('checkmate-api-url') ?? getApiUrl()) : getApiUrl()
}

/**
 * Checks Direct RCM connection status with keep-alive mechanism
 *
 * @param firstDef - Promise to resolve when connection is established (for initial connection)
 * @param justInitialized - If true, we're in the middle of iframe auth flow, just keep validating
 */
async function checkDRCMConnectionStatus(
  firstDef?: { resolve: () => void; reject: (error: Error) => void },
  justInitialized = false
): Promise<void> {
  return new Promise((resolve, reject) => {
    const onFail = () => {
      nrOfFailedConnections++
      window.dispatchEvent(new CustomEvent('disconnectedFromDRCM'))

      // Reconnect with exponential backoff (max 100 attempts)
      if (nrOfFailedConnections < 100) {
        keepAliveTimeout = setTimeout(() => {
          if (keepAliveTimeout) {
            keepAliveTimeout = null

            // Legacy behavior: If justInitialized=true, keep validating (we're in iframe auth flow)
            // If justInitialized=false, create iframe to re-authenticate
            if (justInitialized) {
              checkDRCMConnectionStatus(firstDef, true).then(resolve).catch(reject)
            } else {
              // Pass firstDef to createConnectionWithDRCM so it can resolve the original promise
              createConnectionWithDRCM(firstDef).then(resolve).catch(reject)
            }
          }
        }, 500)
      } else {
        const error = new Error('Max reconnection attempts reached')
        if (firstDef) {
          firstDef.reject(error)
        }
        reject(error)
      }
    }

    // Use validateToken from auth.ts with useDirectRCMInstance=true
    // This disables c_selections and uses the api-url attribute
    import('./api/auth')
      .then(({ authApiService }) => authApiService.validateToken(true))
      .then((data) => {
        if (data === SPRING_ERROR_MSG) {
          onFail()
        } else {
          // Resolve the first deferred if provided (initial connection)
          if (firstDef) {
            firstDef.resolve()
          }

          resolve()
          nrOfFailedConnections = 0

          // Keep alive - validate connection every 30 seconds
          keepAliveTimeout = setTimeout(() => {
            if (keepAliveTimeout) {
              keepAliveTimeout = null
              checkDRCMConnectionStatus()
            }
          }, KEEP_ALIVE_INTERVAL)
        }
      })
      .catch(onFail)
  })
}

/**
 * Creates Direct RCM connection via hidden iframe
 * @param firstDef - Optional deferred promise from initial connection attempt
 */
async function createConnectionWithDRCM(firstDef?: {
  resolve: () => void
  reject: (error: Error) => void
}): Promise<void> {
  return new Promise((resolve, reject) => {
    // Import getTenant dynamically to avoid circular dependency
    import('./auth')
      .then(({ getTenant }) => {
        const tenantUuid = getTenant()
        if (!tenantUuid) {
          const error = new Error('No tenantUuid found')
          if (firstDef) firstDef.reject(error)
          reject(error)
          return
        }

        let apiUrl = getApiUrl()

        // Ensure it's absolute
        if (!apiUrl.startsWith('http://') && !apiUrl.startsWith('https://')) {
          apiUrl = `https://${apiUrl}`
        }

        const param1 = `${apiUrl}/sso/imedidata?tenantUuid=${tenantUuid}&directRedirectTo=${apiUrl}/api?tenantUuid=${tenantUuid}`
        const loginUrl = `${getIMedidataLoginUrl()}/login?service=${encodeURIComponent(param1)}`

        const iframe = document.createElement('iframe')
        iframe.src = loginUrl
        iframe.style.width = '0'
        iframe.style.height = '0'
        iframe.style.display = 'none'
        iframe.frameBorder = '0'

        iframe.onload = () => {
          // Create or use existing deferred promise for iframe auth flow
          // This matches legacy: def = def ? def : $q.defer()
          const iframeDef = firstDef || { resolve, reject }

          // Pass deferred and justInitialized=true to indicate we're in iframe auth flow
          // Legacy: checkDRCMConnectionStatus(def, true)
          checkDRCMConnectionStatus(iframeDef, true)
            .then(() => {
              window.dispatchEvent(new CustomEvent('connectedToDRCM'))
              iframe.remove()
              if (!firstDef) {
                // Only resolve local promise if we didn't get one from caller
                resolve()
              }
            })
            .catch((error) => {
              console.error('Failed to authenticate with Direct RCM:', error)
              iframe.remove()
              if (!firstDef) {
                reject(error)
              }
            })
        }

        document.body.appendChild(iframe)
      })
      .catch((error) => {
        if (firstDef) firstDef.reject(error)
        reject(error)
      })
  })
}

/**
 * Initialize connection with Direct RCM (executed only once)
 */
let isInitialized = false
export const initializeConnectionWithDirectRCM = async (): Promise<void> => {
  if (isInitialized) {
    return Promise.resolve()
  }
  isInitialized = true

  if (keepAliveTimeout) {
    clearTimeout(keepAliveTimeout)
    keepAliveTimeout = null
  }

  // Check if we're returning from full-page authentication redirect
  const authInProgress = sessionStorage.getItem('drcm_auth_in_progress')
  if (authInProgress) {
    // Clean up session storage
    sessionStorage.removeItem('drcm_auth_in_progress')
    sessionStorage.removeItem('drcm_auth_return_url')

    // Give browser a moment to fully process cookies after redirect
    await new Promise((resolve) => setTimeout(resolve, 500))
  }

  // Create deferred promise like legacy app does
  // This is critical: we must pass firstDef to checkDRCMConnectionStatus
  // so it can trigger iframe creation on validation failure
  return new Promise((resolve, reject) => {
    const firstDef = { resolve, reject }

    checkDRCMConnectionStatus(firstDef, false).catch((error) => {
      isInitialized = false // Reset on failure to allow retry
      reject(error)
    })
  })
}

// =============================================================================
// CHECKMATE INTEGRATION FUNCTIONS
// =============================================================================

/**
 * Checkmate selection parameter keys
 */
export const CSELECTION_PARAMS = {
  cds: 'com:mdsol:client_division_schemes',
  studies: 'com:mdsol:studies',
  environments: 'com:mdsol:study_environments',
  sites: 'com:mdsol:study_environment_sites',
  actions: 'com:mdsol:actions'
} as const

/**
 * Folder level types for MCC objects
 */
const FOLDER_LEVEL = {
  division: 'CLIENT_DIVISION_SCHEME',
  study: 'STUDY',
  env: 'STUDY_ENVIRONMENT',
  country: 'STUDY_ENVIRONMENT_COUNTRY',
  site: 'STUDY_ENVIRONMENT_SITE'
} as const

/**
 * Current MCC object state
 */
let currentMccObj: Record<string, any> = {}

/**
 * Last c_selection params state
 */
let lastCSelectionParams: Record<string, string> = {}

/**
 * Last emitted params (to prevent duplicate emissions)
 */
let lastEmittedParams: Record<string, string> | null = null

/**
 * Get c_selections string from MUI
 */
export const getCSelections = (): string => {
  if (!isCheckmated()) {
    return ''
  }

  let cSelections = ''
  const global = globalThis as any

  if (isCheckmated() && typeof global.MUI?.currentCSelections === 'function') {
    try {
      cSelections = global.MUI.currentCSelections()
    } catch (err) {
      console.log('Error in MUI:', err)
    }
  }

  return cSelections
}

/**
 * Parse c_selections string into params object
 */
export const parseCSelectionParams = (cSelections?: string): Record<string, string> => {
  const selections = cSelections ?? getCSelections()
  const params = selections.match(/com:mdsol:.*?([,#]|$)/g)

  if (!params) {
    return {}
  }

  return params.reduce(
    (ob, param) => {
      const cleanParam = param.replace(',', '').replace('#', '')

      const keyMatches = cleanParam.match(/com:mdsol:.*:/g)
      if (keyMatches && keyMatches.length > 0) {
        let key = keyMatches[0]
        const val = cleanParam.replace(key, '')
        key = key.substr(0, key.length - 1)
        ob[key] = val
      }

      return ob
    },
    {} as Record<string, string>
  )
}

/**
 * Get c_selections params object
 */
export const getCSelectionsParams = (): Record<string, string> => {
  return parseCSelectionParams()
}

/**
 * Parse site UUID from c_selections
 */
export const parseSites = (cSelections?: string): string | null => {
  const selections = cSelections ?? getCSelections()
  const cSelString = selections.match(/com:mdsol:study_environment_sites:(.{36})/)
  return Array.isArray(cSelString) && cSelString.length === 2 ? cSelString[1] : null
}

/**
 * Parse client division scheme ID from c_selections
 */
export const parseClientDivisionSchemeId = (cSelections?: string): string | null => {
  const selections = cSelections ?? getCSelections()
  const cSelString = selections.match(/com:mdsol:client_division_schemes:(.{36})/)
  return Array.isArray(cSelString) && cSelString.length === 2 ? cSelString[1] : null
}

/**
 * Parse query parameters from URL
 */
export const parseQueryParams = (url: string): Record<string, string> => {
  const queryStringMatch = url.match(/\?.*?(#|$)/g)
  const params: Record<string, string> = {}

  if (queryStringMatch && queryStringMatch[0]) {
    let queryString = queryStringMatch[0]
    queryString = queryString.replace('?', '').replace('#', '')
    const parts = queryString.split('&')

    for (let i = 0; i < parts.length; i++) {
      const param = parts[i].split('=')
      const key = param[0].toLowerCase()
      const value = param.length > 1 ? param[1] : ''
      params[decodeURIComponent(key)] = decodeURIComponent(value)
    }
  } else {
    console.log('no query string')
  }

  return params
}

/**
 * Add c_selections to a URL
 */
export const addCSelections = (link: string, params?: Record<string, string | null>): string => {
  if (isCheckmated()) {
    const customCSelectionParams = getBlankParams()
    if (params) {
      Object.assign(customCSelectionParams, params)
    }
    return link + (link.indexOf('?') > -1 ? '&' : '?') + buildCSelections(customCSelectionParams)
  }
  return link
}

/**
 * Get study URL with c_selections
 */
export const getStudyUrl = (obUuid: string): string => {
  if (!isCheckmated()) {
    throw new Error('unsupported action for uncheckmated site')
  }

  const domain = getDomain()
  const customParams = getBlankParams()
  customParams[CSELECTION_PARAMS.studies] = obUuid
  customParams[CSELECTION_PARAMS.actions] = 'show_study'
  const customCSelections = buildCSelections(customParams)

  return `${domain}/apps/admin/studies/${obUuid}/edit?${customCSelections}`
}

/**
 * Get site URL with c_selections
 */
export const getSiteUrl = (parentObUuid: string, obUuid: string): string => {
  if (!isCheckmated()) {
    throw new Error('unsupported action for uncheckmated site')
  }

  const domain = getDomain()
  const customCSelectionParams = getBlankParams()
  customCSelectionParams[CSELECTION_PARAMS.studies] = parentObUuid
  customCSelectionParams[CSELECTION_PARAMS.actions] = 'read_studies'
  const customCSelections = buildCSelections(customCSelectionParams)

  return `${domain}/apps/adminext/studies/${parentObUuid}/sites?${customCSelections}#/studies/${parentObUuid}/sites/${obUuid}`
}

/**
 * Initialize checkmate state
 */
export const onCheckmateInit = (): void => {
  lastCSelectionParams = parseCSelectionParams()
}

/**
 * Maps new React app stateName values → their Checkmate action suffix.
 *
 * HOW IT WORKS:
 *   Full action emitted = contextPathUriName + '-' + suffix  (e.g. 'rcm-browser')
 *   Legacy built this via:  stateName.replace('app.', '').replaceAll('.', '-')
 *   ...but ONLY for states registered in helpPageService (= actions.yml keys).
 *   Unregistered states fell back to just contextPathUriName (e.g. 'rcm').
 *
 * SOURCE OF TRUTH: assets/docs/actions.yml
 *   The keys in that file (after removing 'edge-rcm-' prefix) are the exact suffixes.
 *   e.g.  edge-rcm-browser-templates  →  suffix 'browser-templates'
 *
 * MAPPING:
 *   New stateName           │ Legacy state               │ actions.yml key suffix
 *   ────────────────────────┼────────────────────────────┼──────────────────────────────────
 *   --- onStateChange (root.tsx: pathSegments[0]) ---
 *   'overview'              │ app.dashboard              │ dashboard
 *   'search'                │ app.search                 │ search
 *   'clinical-status'       │ app.etmf.status            │ etmf-status
 *   'saved-report-settings' │ app.savedReportSettings    │ savedReportSettings
 *   --- DocumentsListPage.tsx: emitCustomCheckmateAction ---
 *   'browser'               │ app.browser                │ browser
 *   'browser.templates'     │ app.browser.templates      │ browser-templates
 *   'browser.systemDocuments'│app.browser.systemDocuments│ browser-systemDocuments
 *   'browser.forms'         │ app.browser.forms          │ browser-forms
 *   'reports.propertySearch'│ app.reports.propertySearch │ reports-propertySearch
 *   'browser.periodicReviewUpcoming'│app.browser.periodicReviewUpcoming│browser-periodicReviewUpcoming
 *   'browser.recentDocuments'│app.browser.recentDocuments│ browser-recentDocuments
 *   --- stagingService.emitCheckmateAction ---
 *   'staging-stagingArea'   │ app.staging.stagingArea    │ staging-stagingArea
 */
const ROUTE_TO_ACTION_SUFFIX: Record<string, string> = {
  // Page routes (via onStateChange in root.tsx)
  overview: 'dashboard',
  search: 'search',
  'clinical-status': 'etmf-status',
  'saved-report-settings': 'savedReportSettings',

  // Browser view states (via DocumentsListPage.tsx → emitCustomCheckmateAction)
  browser: 'browser',
  'browser.templates': 'browser-templates',
  'browser.systemDocuments': 'browser-systemDocuments',
  'browser.forms': 'browser-forms',
  'reports.propertySearch': 'reports-propertySearch',
  'browser.periodicReviewUpcoming': 'browser-periodicReviewUpcoming',
  'browser.recentDocuments': 'browser-recentDocuments',

  // Staging (via stagingService.emitCheckmateAction → emitCustomCheckmateAction/emitDefaultCheckmateAction)
  'staging-stagingArea': 'staging-stagingArea'
  // NOTE: 'browser' and 'show-file' are excluded from onStateChange (skip list in onStateChange)
  // NOTE: 'staging' is routed to stagingService.emitCheckmateAction() which uses 'staging-stagingArea'
}

/**
 * Transform state name to action name
 */
export const transformStateToAction = (stateName?: string): string => {
  const apiUrl = getApiUrl()
  const lastIndex = apiUrl.lastIndexOf('/')
  const secondLastIndex = apiUrl.lastIndexOf('/', lastIndex - 1)
  const contextPathUriName = apiUrl.substring(secondLastIndex + 1, lastIndex)

  let actionName = contextPathUriName

  if (stateName) {
    // Only append if the route has a registered action suffix (mirrors legacy hasStateHelpUrl guard).
    // Unregistered routes emit just contextPathUriName — same as legacy fallback.
    const suffix = ROUTE_TO_ACTION_SUFFIX[stateName]
    if (suffix) {
      actionName += '-' + suffix
    } else {
      console.log('### stateName is not registered as checkmate action:', stateName)
    }
  }

  return actionName
}

/**
 * Listen to checkmate header changes
 */
export const listenToCheckmateHeaderChanges = (callbackFn?: (err: any, newCSelections: string) => void): void => {
  if (!isCheckmated()) {
    return
  }

  const global = globalThis as any

  global.MUI?.onNavUpdated((err: any, newCSelections: string) => {
    const newCSelectionParams = parseCSelectionParams(newCSelections)
    console.log('### NAV UPDATED', newCSelectionParams)

    if (typeof callbackFn === 'function') {
      callbackFn(err, newCSelections)
    }

    if (err) {
      console.log('Checkmate Header Click Error:', err)
      return
    }

    if (newCSelectionParams[CSELECTION_PARAMS.cds] !== lastCSelectionParams[CSELECTION_PARAMS.cds]) {
      // Only reload if we're actually SWITCHING between two different CDS (organizations).
      // If lastCSelectionParams has no CDS, this is the first time CDS is being populated
      // (e.g. MUI fires onNavUpdated with CDS during/after navigateSpaAction before the
      // Promise resolves and updates lastCSelectionParams). Not a real org switch — proceed normally.
      if (lastCSelectionParams[CSELECTION_PARAMS.cds]) {
        reloadPageWithDefaultCSelection()
        return
      }
    }

    reactToCSelections(newCSelectionParams)
  })
}

/**
 * React to c_selections changes
 */
export const reactToCSelections = async (newCSelectionParams?: Record<string, string>): Promise<void> => {
  if (!isCheckmated()) {
    return
  }

  const params = newCSelectionParams ?? parseCSelectionParams()
  const actionName = readAction(params)

  const apiUrl = getApiUrl()
  const lastIndex = apiUrl.lastIndexOf('/')
  const secondLastIndex = apiUrl.lastIndexOf('/', lastIndex - 1)
  const contextPathUriName = apiUrl.substring(secondLastIndex + 1, lastIndex)

  let clinicalFolder: { uuid: string | null; type: string } = { uuid: null, type: '' }

  if (actionName === `${contextPathUriName}-study-environments`) {
    const envUuid = readEnvironment(params)
    if (envUuid) {
      clinicalFolder = {
        uuid: envUuid,
        type: FOLDER_LEVEL.env
      }
    }
  }

  if (!clinicalFolder.uuid) {
    const siteUuid = readSites(params)
    if (siteUuid) {
      clinicalFolder = {
        uuid: siteUuid,
        type: FOLDER_LEVEL.site
      }
    }
  }

  if (!clinicalFolder.uuid) {
    const studyUuid = readStudies(params)
    if (studyUuid) {
      clinicalFolder = {
        uuid: studyUuid,
        type: FOLDER_LEVEL.study
      }
    }
  }

  lastCSelectionParams = params

  if (clinicalFolder.uuid) {
    // Import ApiEtmf dynamically to avoid circular dependency
    const { getFsiIdsFromClinicalUuids } = await import('./api/etmf')

    try {
      const matchingFolders = await getFsiIdsFromClinicalUuids([clinicalFolder.uuid])

      if (matchingFolders.length > 0) {
        // Legacy: first try to find by mccObjType + app prefix, then fall back to [0]
        const integrationPrefix = currentMccObj?.app || 'ETMF'
        let etmfFsiObject = matchingFolders.find(
          (f: any) => f.mccObjType === clinicalFolder.type && f.app === integrationPrefix
        )
        if (!etmfFsiObject) {
          etmfFsiObject = matchingFolders[0]
        }

        if (etmfFsiObject) {
          console.log('### REDIRECT TO FOLDER', etmfFsiObject, matchingFolders)

          // Import auth and navigate dynamically to avoid circular dependency
          const { getRole } = await import('./auth')
          const { serviceNavigate } = await import('./navigation')
          const userRole = getRole()

          // Reset lastEmittedParams before navigating.
          // Legacy used $state.go(..., {reload: true}) which fully reloads the page state,
          // resetting all module-level variables including lastEmittedParams / lastCSelectionParams.
          // Without this reset, re-selecting the same study from the Checkmate header sends
          // MUI nav event → reactToCSelections → navigates to same folder → browser page calls
          // emitCustomCheckmateAction → cSelectionContains returns true (MUI already has rcm-browser
          // + same study UUID from prior emission) → skips emit → header never re-renders.
          lastEmittedParams = null

          // Role-based navigation matching legacy checkmate.service.js:
          //   app.staging.stagingArea → /staging/stagingarea
          //   app.staging.overview   → /staging/overview
          if (userRole === 'ETMF_SITE_USER' && clinicalFolder.type === FOLDER_LEVEL.site) {
            serviceNavigate(`/staging/stagingarea?siteId=${etmfFsiObject.fsiId}`)
          } else if (userRole === 'ETMF_STUDY_USER' && clinicalFolder.type === FOLDER_LEVEL.env) {
            serviceNavigate(`/staging/stagingarea?environmentId=${etmfFsiObject.fsiId}`)
          } else if (userRole === 'ETMF_SITE_USER' || userRole === 'ETMF_STUDY_USER') {
            serviceNavigate('/staging/overview')
          } else {
            // All other roles — redirect to browser folder
            serviceNavigate(`/browser/${etmfFsiObject.fsiId}`)
          }
        }
      } else {
        console.log('### no matching folder found')
      }
    } catch (error) {
      console.error('Error fetching FSI IDs:', error)
    }
  }
}

/**
 * Emit default checkmate action
 */
export const emitDefaultCheckmateAction = async (stateName?: string): Promise<void> => {
  if (isCheckmated()) {
    const actionName = transformStateToAction(stateName)
    const defaultCSel = getDefaultCSelections(actionName)
    return emitCheckmateAction(defaultCSel)
  }
}

/**
 * Emit custom checkmate action based on FSI object
 */
export const emitCustomCheckmateAction = async (fsiObject: any, stateName?: string): Promise<void> => {
  if (!isCheckmated()) {
    return
  }

  currentMccObj = {}

  const fullFolderPath = fsiObject.path ? [...fsiObject.path] : []
  fullFolderPath.push(fsiObject)
  console.log('### inspecting folder path', fullFolderPath)

  const fullFolderPathReverse = [...fullFolderPath].reverse()

  const etmfInfo = {
    appFolder: fullFolderPathReverse.find((folderItem) => [FOLDER_LEVEL.division].includes(folderItem.mccObjType)),
    studyFolder: fullFolderPathReverse.find((folderItem) => [FOLDER_LEVEL.study].includes(folderItem.mccObjType)),
    envFolder: fullFolderPathReverse.find((folderItem) => [FOLDER_LEVEL.env].includes(folderItem.mccObjType)),
    countryFolder: fullFolderPathReverse.find((folderItem) => [FOLDER_LEVEL.country].includes(folderItem.mccObjType)),
    siteFolder: fullFolderPathReverse.find((folderItem) => [FOLDER_LEVEL.site].includes(folderItem.mccObjType))
  }

  let cSelParams: Record<string, string | null> | null = null

  if (etmfInfo.studyFolder) {
    const apiUrl = getApiUrl()
    const lastIndex = apiUrl.lastIndexOf('/')
    const secondLastIndex = apiUrl.lastIndexOf('/', lastIndex - 1)
    const contextPathUriName = apiUrl.substring(secondLastIndex + 1, lastIndex)

    const etmfFolderParams = getBlankParams()
    etmfFolderParams[CSELECTION_PARAMS.studies] = etmfInfo.studyFolder.mccObjUuid
    etmfFolderParams[CSELECTION_PARAMS.actions] = `${contextPathUriName}-studies`
    currentMccObj = etmfInfo.studyFolder

    if (etmfInfo.envFolder) {
      etmfFolderParams[CSELECTION_PARAMS.environments] = etmfInfo.envFolder.mccObjUuid
      etmfFolderParams[CSELECTION_PARAMS.actions] = `${contextPathUriName}-study-environments`
      currentMccObj = etmfInfo.envFolder

      if (etmfInfo.siteFolder) {
        etmfFolderParams[CSELECTION_PARAMS.sites] = etmfInfo.siteFolder.mccObjUuid
        etmfFolderParams[CSELECTION_PARAMS.actions] = `${contextPathUriName}-study-environment-sites`
        currentMccObj = etmfInfo.siteFolder
      }
    }

    if (etmfInfo.appFolder) {
      etmfFolderParams[CSELECTION_PARAMS.cds] = etmfInfo.appFolder.mccObjUuid
    }

    if (stateName) {
      const actionName = transformStateToAction(stateName)
      const defaultActionName = transformStateToAction()
      if (actionName && actionName !== defaultActionName) {
        etmfFolderParams[CSELECTION_PARAMS.actions] = actionName
      }
    }

    cSelParams = etmfFolderParams
  }

  if (cSelParams) {
    // Legacy guard: skip emission only if current MUI c_selections already contain ALL our
    // params AND we haven't just reset lastEmittedParams (i.e. no pending navigation).
    // When lastEmittedParams is null it means reactToCSelections just triggered a navigation
    // (equivalent to legacy's $state.go reload=true) — we must always emit in that case
    // so the browser page action (e.g. rcm-browser) is registered with MUI after navigation.
    if (lastEmittedParams === null || !cSelectionContains(cSelParams)) {
      const updatedCSelections = buildCSelectionsString(cSelParams)
      return emitCheckmateAction(updatedCSelections)
    }
  } else {
    // No studyFolder found in path — the /path and /detail API endpoints don't return
    // mccObjType/mccObjUuid for path items so FOLDER_LEVEL matching always fails here.
    //
    // IMPORTANT: do NOT call emitDefaultCheckmateAction() directly because it uses
    // getBlankParams() → { studies: null, environments: null, sites: null, ... }
    // which explicitly overrides (and therefore strips) any study UUID that MUI currently
    // has in its c_selections. This would clear the study from the Checkmate header.
    //
    // Instead, check lastCSelectionParams (set by reactToCSelections before navigation)
    // which still holds the study UUID the user just selected. Build the browser action
    // c_selections from that, preserving the study context.
    const studyUuid = lastCSelectionParams[CSELECTION_PARAMS.studies]

    if (studyUuid) {
      // Build c_selections from lastCSelectionParams (which has study/env/site context)
      // only overriding the action to match the current page (e.g. edge-rcm-browser).
      const preservedParams: Record<string, string | null> = { ...lastCSelectionParams }
      if (stateName) {
        const actionName = transformStateToAction(stateName)
        const defaultActionName = transformStateToAction()
        if (actionName && actionName !== defaultActionName) {
          preservedParams[CSELECTION_PARAMS.actions] = actionName
        }
      }
      if (lastEmittedParams === null || !cSelectionContains(preservedParams)) {
        const updatedCSelections = buildCSelectionsString(preservedParams)
        return emitCheckmateAction(updatedCSelections)
      }
    } else {
      // No study in lastCSelectionParams — no Checkmate header study selected yet.
      // Fall back to default action (no study context to preserve).
      return emitDefaultCheckmateAction(stateName)
    }
  }
}

/**
 * Emit checkmate action to MUI
 *
 * Legacy note: skipped entirely on Edge browser due to known infinite loop issue
 * (checkmated rcm in edge will get into infinite loop on location update)
 */
export const emitCheckmateAction = async (newCSelections: string): Promise<void> => {
  if (!isCheckmated()) {
    return
  }

  // Legacy guard: skip on Edge browser to avoid infinite loop on location update
  const { detectBrowser } = await import('../helpers/generalHelper')
  if (detectBrowser().edge) {
    return
  }

  const global = globalThis as any

  // Use $timeout(0) equivalent: defer to next tick, then resolve after navigateSpaAction completes.
  // Outer try/catch matches legacy: $timeout scheduling errors resolve the deferred immediately.
  try {
    await new Promise<void>((resolve) => {
      setTimeout(async () => {
        // finally ensures resolve() is always called (matches legacy muiAction.always(def.resolve))
        try {
          const newCSelectionsParams = parseCSelectionParams(newCSelections)

          if (!cSelectionEquals(newCSelectionsParams) && !cSelectionEquals(newCSelectionsParams, lastEmittedParams)) {
            lastEmittedParams = { ...newCSelectionsParams }
            console.log('### CHECKMATE ACTION - EMITTING', newCSelectionsParams, newCSelections)

            try {
              const updatedCSelections = await global.MUI.navigateSpaAction(newCSelections)
              const updatedCSelectionParams = parseCSelectionParams(updatedCSelections)
              lastCSelectionParams = updatedCSelectionParams
              console.log(
                '### CHECKMATE ACTION - EMITTED: ' + updatedCSelectionParams[CSELECTION_PARAMS.actions],
                updatedCSelectionParams
              )
            } catch (err) {
              console.error('[CHECKMATE] Error calling MUI.navigateSpaAction:', err)
            }
          } else {
            console.log('### CHECKMATE ACTION - SKIPPED', newCSelectionsParams)
          }
        } finally {
          // Always resolve — matches legacy muiAction.always(def.resolve)
          resolve()
        }
      }, 0)
    })
  } catch (err) {
    // Matches legacy outer try/catch: $timeout scheduling errors resolve the deferred immediately
    console.error('[CHECKMATE] Error in emitCheckmateAction:', err)
  }
}

/**
 * Handle state changes for checkmate
 * This is called when React Router navigates to a new route
 * Legacy equivalent: checkmate.service.js onStateChange()
 */
export const onStateChange = async (toState: { name?: string }): Promise<void> => {
  if (!isCheckmated()) {
    return
  }

  const toStateName = toState?.name

  // Skip browser and show-file routes (legacy: ['app.browser', 'app.showFile'])
  if (!toStateName || ['browser', 'show-file'].includes(toStateName)) {
    return
  }

  // Ensure checkmateActionPromise is initialized — wait for checkmate platform to be ready.
  // Legacy: checkmateActionDef = initService.waitForCheckmate()
  if (!checkmateActionPromise) {
    const { initService } = await import('./init.service')
    checkmateActionPromise = initService.waitForCheckmate().then(() => undefined)
  }

  // In legacy, the 'app' state's resolve includes waitForCheckmate. This means:
  //   1. waitForCheckmate resolves (MUI ready)
  //   2. AppLayoutController runs → listenToCheckmateHeaderChanges() registers onNavUpdated
  //   3. MUI fires onNavUpdated with initial c_selections (study UUID from header)
  //   4. reactToCSelections → $state.go('app.browser', {fsiId}) — redirect to browser
  //   5. $stateChangeSuccess fires for 'app.browser' — which is in the SKIP list
  //   6. Result: onStateChange NEVER fires for the initial route → study preserved
  //
  // In the new code, the root.tsx useEffect fires onStateChange('overview') BEFORE
  // onNavUpdated/reactToCSelections has had a chance to redirect. This emits
  // edge-rcm-dashboard with no study → clears the study from the header.
  //
  // Fix: after waiting for checkmateActionPromise, check if we're still on the same route.
  // If reactToCSelections redirected us during the wait, discard the stale action.

  const def = new Promise<void>((resolve) => {
    const onReady = async () => {
      try {
        // Guard: check if the route has changed during the wait (e.g., reactToCSelections
        // redirected to /browser/fsiId). If so, this onStateChange call is stale; the
        // new route's own useEffect will handle its action (or skip if it's 'browser').
        //
        // NOTE: onStateChange only runs in checkmated (hash-routing) mode — isCheckmated()
        // is the first guard above. In hash routing, window.location.pathname is the legacy
        // server path (e.g. '/rcm/app') and never changes; the React route lives in the hash.
        // Parse the current React route from window.location.hash instead, stripping the
        // createHashRouter basename '/!/app' (configured in entry.client.tsx).
        const hashPath = window.location.hash
          .replace(/^#/, '') // '#/!/app/browser/123' → '/!/app/browser/123'
          .replace(/^\/!\/app/, '') // '/!/app/browser/123' → '/browser/123'
          .split('?')[0] // strip query string inside hash
        const currentRoute = hashPath.split('/').filter(Boolean)[0] || 'home'
        if (currentRoute !== toStateName) {
          resolve()
          return
        }

        if (toStateName.startsWith('staging')) {
          const { stagingService } = await import('./staging.service')
          checkmateActionPromise = stagingService.emitCheckmateAction()
          await checkmateActionPromise
        } else {
          checkmateActionPromise = emitDefaultCheckmateAction(toStateName)
          await checkmateActionPromise
        }
      } catch (error) {
        console.error('[CHECKMATE] Error in onStateChange:', error)
      }
      resolve()
    }
    // Chain after existing promise (both resolve and reject trigger — matches legacy .then(fn, fn)).
    // IMPORTANT: assign the result back to checkmateActionPromise so that each successive
    // onStateChange call chains onto the tail of the previous, keeping emissions serialized.
    // Without this assignment, rapid route changes attach multiple onReady handlers to the
    // same promise and run them concurrently rather than sequentially.
    checkmateActionPromise = checkmateActionPromise!.then(onReady, onReady)
  })

  return def
}

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

const getDomain = (): string => {
  return window.location.origin
}

const readStudies = (params: Record<string, string>): string | null => {
  return params['com:mdsol:studies'] ?? null
}

const readAction = (params: Record<string, string>): string | null => {
  return params['com:mdsol:actions'] ?? null
}

const readEnvironment = (params: Record<string, string>): string | null => {
  return params['com:mdsol:study_environments'] ?? null
}

const readSites = (params: Record<string, string>): string | null => {
  return params['com:mdsol:study_environment_sites'] ?? null
}

const reloadPageWithDefaultCSelection = (): void => {
  const defaultCSelect = getDefaultCSelections()
  const newLocationHref = window.location.href.replace(/c_selections=.*$/, 'c_selections=' + defaultCSelect)
  window.location.href = newLocationHref
}

const getDefaultCSelections = (actionName?: string): string => {
  const defaultCSelParams = getBlankParams()
  const apiUrl = getApiUrl()
  const lastIndex = apiUrl.lastIndexOf('/')
  const secondLastIndex = apiUrl.lastIndexOf('/', lastIndex - 1)
  const contextPathUriName = apiUrl.substring(secondLastIndex + 1, lastIndex)
  defaultCSelParams[CSELECTION_PARAMS.actions] = actionName ?? contextPathUriName
  return buildCSelectionsString(defaultCSelParams)
}

const buildCSelections = (customParams: Record<string, string | null>): string => {
  return 'c_selections=' + buildCSelectionsString(customParams)
}

const buildCSelectionsString = (customParams: Record<string, string | null>): string => {
  const cselectionParams = parseCSelectionParams()
  const params = { ...cselectionParams, ...customParams }

  // Filter out null values
  const filteredParams = Object.entries(params).reduce(
    (acc, [key, value]) => {
      if (value !== null) {
        acc[key] = value
      }
      return acc
    },
    {} as Record<string, string>
  )

  return Object.entries(filteredParams)
    .map(([key, value]) => `${key}:${value}`)
    .join(',')
}

const cSelectionContains = (
  params: Record<string, string | null>,
  cSelectionParams?: Record<string, string>
): boolean => {
  const currentParams = cSelectionParams ?? parseCSelectionParams()
  // Skip null-valued entries — they represent "not set" and are filtered out by
  // buildCSelectionsString before being sent to MUI. Comparing them against MUI's
  // current params (where the key is simply absent) would always return false,
  // making the "already emitted" guard permanently ineffective for any cSelParams
  // built from getBlankParams() (which initialises environments/sites/studies to null).
  return Object.entries(params).every(([key, val]) => {
    if (val === null) return true // not part of the actual emitted c_selections — skip
    const currentVal = currentParams[key]
    return !!currentVal && currentVal === val
  })
}

const cSelectionEquals = (
  params: Record<string, string>,
  cSelectionParams?: Record<string, string> | null
): boolean => {
  const currentParams = cSelectionParams ?? parseCSelectionParams()
  // Use entry-by-entry comparison (like legacy _.isEqual) — JSON.stringify is order-sensitive
  // which causes false "not equal" when key ordering differs between parseCSelectionParams calls.
  const paramsKeys = Object.keys(params)
  const currentKeys = Object.keys(currentParams)
  if (paramsKeys.length !== currentKeys.length) return false
  return paramsKeys.every((key) => params[key] === currentParams[key])
}

const getBlankParams = (): Record<string, string | null> => {
  return Object.values(CSELECTION_PARAMS)
    .filter((key) => key !== CSELECTION_PARAMS.cds)
    .reduce(
      (mem, key) => {
        mem[key] = null
        return mem
      },
      {} as Record<string, string | null>
    )
}

// =============================================================================
// EXPORTED CHECKMATE SERVICE OBJECT
// =============================================================================

export const checkmateService = {
  // Core functions
  isCheckmated,
  getApiUrl,
  getIMedidataLoginUrl,
  getCheckmatedApiUrl,
  initializeConnectionWithDirectRCM,

  // C-Selections functions
  getCSelections,
  getCSelectionsParams,
  parseCSelectionParams,
  addCSelections,
  parseSites,
  parseClientDivisionSchemeId,

  // URL builders
  getStudyUrl,
  getSiteUrl,

  // Navigation and actions
  onCheckmateInit,
  onStateChange,
  listenToCheckmateHeaderChanges,
  reactToCSelections,
  emitCheckmateAction,
  emitCustomCheckmateAction,
  emitDefaultCheckmateAction,
  transformStateToAction,

  // Utilities
  parseQueryParams,

  // Constants
  CSELECTION_PARAMS
}

export default checkmateService
